// Was a relative '/api' path, relying on a Cloudflare Pages _redirects rule to proxy it
// to the real backend on a different domain. Pages' _redirects only honors a 200 (rewrite)
// status for paths within the same Pages project -- it doesn't actually proxy cross-origin
// to api.aetheris.fyi despite the rule's syntax being accepted, so every non-GET request
// (login included) hit the static host directly and got a plain 405. go-api's CORS is
// already wide open (echoes any Origin, allows all methods + credentials), so calling the
// real API domain directly works with no backend change needed.
const BASE_URL = 'https://api.aetheris.fyi/api';
const SESSION_TOKEN_KEY = 'aetheris_session_token';
const IS_ADMIN_KEY = 'aetheris_is_admin';

// Pi power-API, reached via its own Cloudflare Tunnel (see wake.aetheris.fyi).
// This is deliberately called directly from the client -- go-api can't be the one
// to trigger a wake, since go-api only runs once the server it lives on is already
// awake. The API key here is scoped to this one Pi endpoint (rate-limited, wake/
// heartbeat/sleep/status only) -- it is not the backend's session/auth secret.
const WAKE_BASE_URL = 'https://wake.aetheris.fyi';
const WAKE_API_KEY = '8a4fa05f0deefa105f989ac49f5b658e';

// ── SESSION-EXPIRY INTERCEPTOR ───────────────────────────────────────
// 2026-08-20: a Redis restart wiped every stored session server-side, but the
// stored token in each client's localStorage/api_token was untouched -- every
// page just kept firing normal-looking fetch() calls, each one silently
// getting a 401, and every one of those pages' own "no data" empty-state UI
// (built for the ordinary "you have no movies yet" case) rendered instead of
// anything telling the user they'd been logged out. Read as "the whole app
// is broken" rather than "please log in again".
//
// The real fix is here, not per-page: most of this app's ~20+ pages call the
// browser's raw fetch() directly rather than going through request() below
// (MusicPage alone has 39 call sites), so patching request() alone would
// have missed the vast majority of traffic. Wrapping window.fetch itself
// once, globally, is the only fix that actually covers every call site
// without touching 20+ files individually.
//
// Only reacts when: (a) it's a 401, (b) it's our own API (not an unrelated
// third-party fetch elsewhere on the page), (c) it's not the login/signup
// endpoints themselves (a wrong password is a normal 401, not a stale-
// session signal), and (d) a token actually existed (a 401 with no token at
// all is just "not logged in yet", the ordinary pre-login state, not an
// expiry). Clears the stale token, stashes a flag LoginForm reads to explain
// what happened, and reloads -- a full reload rather than trying to reset
// React state from outside the component tree, since this runs at module
// scope with no access to App's own session state.
let sessionExpiryHandled = false;
function installSessionExpiryInterceptor() {
  if (typeof window === 'undefined' || window.__aetherisFetchWrapped) return;
  window.__aetherisFetchWrapped = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await originalFetch(...args);
    if (res.status === 401 && !sessionExpiryHandled) {
      const url = (typeof args[0] === 'string' ? args[0] : args[0]?.url) || '';
      const isOwnApi = url.includes('api.aetheris.fyi') || url.startsWith('/api/');
      const isAuthEndpoint = /\/auth\/(login|signup|verify|totp)/.test(url);
      const hadToken = !!getSessionToken();
      if (isOwnApi && !isAuthEndpoint && hadToken) {
        sessionExpiryHandled = true;
        clearSessionToken();
        try { sessionStorage.setItem('aetheris_session_expired', '1'); } catch (e) {}
        window.location.reload();
      }
    }
    return res;
  };
}
installSessionExpiryInterceptor();

// Shared onError handler for the site's inline poster/backdrop <img> tags
// (Movies/TV Shows/Music). Root-caused 2026-08-15: a broken/missing image
// URL (TMDB gap, malformed path, transient CDN error) rendered the browser's
// default broken-image icon with no fallback, distinct from the existing
// "no poster_path at all" case most pages already handle with a text-label
// placeholder div. This reuses that same look for the broken-URL case too --
// hide the <img>, then splice in a centered monospace label matching the
// site's existing empty-poster placeholder styling. `data-poster-fallback`
// guards against onError re-firing in a loop if the DOM manipulation itself
// somehow re-triggers a load attempt.
export function handlePosterImgError(e, label) {
  const img = e.currentTarget;
  if (img.dataset.posterFallback) return;
  img.dataset.posterFallback = '1';
  img.style.display = 'none';
  const parent = img.parentElement;
  if (!parent) return;
  const placeholder = document.createElement('div');
  placeholder.textContent = label || '';
  Object.assign(placeholder.style, {
    width: '100%', height: '100%', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#3a3a5a', fontSize: '11px',
    fontFamily: 'monospace', padding: '8px', textAlign: 'center',
  });
  parent.appendChild(placeholder);
}

export function openPlayerWindow(streamUrl, title, { tmdbId, mediaType = 'movie', season, episode, posterPath } = {}) {
  const token = getSessionToken();
  const ownOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  let url;
  if (streamUrl.startsWith(ownOrigin) || streamUrl.startsWith('/')) {
    url = streamUrl;
    if (!url.includes('token=') && token) {
      url += (url.includes('?') ? '&' : '?') + 'token=' + token;
    }
  } else {
    url = `/api/stream/remux?url=${encodeURIComponent(streamUrl)}&token=${token}`;
  }
  const params = new URLSearchParams({ url, title: title || 'Now Playing', token: token || '' });
  if (tmdbId) params.set('tmdb_id', tmdbId);
  if (mediaType) params.set('media_type', mediaType);
  if (season) params.set('season', season);
  if (episode) params.set('episode', episode);
  if (posterPath) params.set('poster_path', posterPath);
  const w = window.open(`/player.html?${params}`, '_blank');
  // Store now-playing in localStorage for TopNav indicator
  // Add to recently watched list (up to 10 items)
  const key = 'aetheris_recently_watched';
  let recent = [];
  try { recent = JSON.parse(localStorage.getItem(key)) || []; } catch {}
  // Remove existing entry for same title
  recent = recent.filter(r => r.title !== title);
  // Add to front
  recent.unshift({ title, url, ts: Date.now() });
  // Keep last 10
  recent = recent.slice(0, 10);
  localStorage.setItem(key, JSON.stringify(recent));
  // Keep legacy key for Dashboard compatibility
  localStorage.setItem('aetheris_now_playing', JSON.stringify({ title, url, ts: Date.now() }));
  window.dispatchEvent(new Event('aetheris_now_playing_changed'));
  return w;
}

export async function hlsStart(movieId) {
  return request(`/movies/${movieId}/hls/start`, { method: 'POST' });
}
export async function hlsStop(movieId, sessionId) {
  return request(`/movies/${movieId}/hls/${sessionId}/stop`, { method: 'POST' });
}
export function hlsPlaylistUrl(movieId, sessionId) {
  const token = getSessionToken();
  return `${BASE_URL}/movies/${movieId}/hls/${sessionId}/playlist.m3u8?token=${token}`;
}
export function buildPlayableUrl(streamUrl) {
  const token = getSessionToken();
  const ownOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  let url;
  if (streamUrl.startsWith(ownOrigin) || streamUrl.startsWith('/')) {
    url = streamUrl;
    if (!url.includes('token=') && token) {
      url += (url.includes('?') ? '&' : '?') + 'token=' + token;
    }
  } else {
    url = `/api/stream/remux?url=${encodeURIComponent(streamUrl)}&token=${token}`;
  }
  return url;
}
export function setSessionToken(token) {
  if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function getSessionToken() {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function clearSessionToken() {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(IS_ADMIN_KEY);
}

// ── SERVER WAKE / SLEEP ──────────────────────────────────────────────
// The Ubuntu server suspends itself when idle. These talk to power_api.py on
// the always-on Pi (via its own Cloudflare Tunnel), never to the Ubuntu-hosted
// backend directly -- that backend can't respond at all while asleep, which is
// exactly the problem this exists to solve.

// Fire-and-forget: ask the Pi to send the wake-on-LAN packet. Never throws --
// a failed wake call shouldn't block the login screen from at least trying,
// and if the server's already awake this is a harmless no-op on the Pi side.
export async function wakeServer() {
  try {
    await fetch(`${WAKE_BASE_URL}/wake`, {
      method: 'GET',
      headers: { 'X-API-Key': WAKE_API_KEY },
    });
  } catch (e) {
    console.warn('wakeServer: request failed (continuing anyway)', e);
  }
}

// Fire-and-forget: tell the Pi someone is still actively using the app.
// power_api.py's monitor_loop suspends the server SLEEP_TIMEOUT_SECONDS
// (120s) after the last heartbeat it saw -- wakeServer() above sets that
// once at login, but nothing ever refreshed it afterward, so the Pi had no
// real signal that the app was still open. It falls back to Ubuntu-level
// checks (active terminal login, load average, a watched process like
// Steam) which don't cover the common case: someone quietly watching a
// direct-play stream with an idle terminal and normal CPU load. Call this
// every HEARTBEAT_INTERVAL_SECONDS (see App.jsx) while a session exists so
// the Pi's own definition of "in use" actually includes "app is open."
// Never throws, same reasoning as wakeServer().
export async function sendHeartbeat() {
  try {
    await fetch(`${WAKE_BASE_URL}/heartbeat`, {
      method: 'GET',
      headers: { 'X-API-Key': WAKE_API_KEY },
    });
  } catch (e) {
    console.warn('sendHeartbeat: request failed (continuing anyway)', e);
  }
}

// Single-shot check of whether the real backend is up yet. Resolves true/false,
// never throws or rejects -- callers poll this in a loop rather than treating
// a failure as fatal, since "not up yet" is the expected state most of the time.
export async function checkServerAwake() {
  try {
    const res = await fetch(`${BASE_URL}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch (e) {
    return false;
  }
}

// Polls checkServerAwake until it returns true or timeoutMs elapses. Resolves
// true/false (never throws). onTick, if given, is called after every attempt
// with the elapsed-ms count so a caller can drive a status readout.
//
// 2026-08-20 (late): also RE-SENDS the wake signal every resendWakeMs while
// waiting, instead of relying on the caller's single up-front wakeServer().
// Root-caused live that night: a magic packet that lands while the server is
// still mid-suspend-TRANSITION (the ~60-75s window where disks are syncing and
// consoles are frozen, before PME wake arming is actually live) is silently
// lost -- the Shield's one-shot wake at 19:04:16 did exactly that, then polled
// a dead server for 90s with no retry. Re-sending is free (rate-limited Pi
// endpoint, idempotent magic packet) and turns that timing lottery into a
// guarantee: some retry always arrives AFTER the transition completes.
export async function waitForServerAwake({ intervalMs = 2000, timeoutMs = 120000, onTick, resendWakeMs = 10000 } = {}) {
  const start = Date.now();
  let lastWakeSent = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (await checkServerAwake()) return true;
    if (resendWakeMs && Date.now() - lastWakeSent >= resendWakeMs) {
      lastWakeSent = Date.now();
      wakeServer();
    }
    if (onTick) onTick(Date.now() - start);
    await new Promise(r => setTimeout(r, intervalMs));
  }
  return false;
}

// Reads the wall-power-outage flag go-api computes from watching the Pi (see
// power_monitor.go) -- public/no-auth, safe to call before a session exists.
// Never throws: a failed call here just means "unknown," not "outage," since
// go-api itself being unreachable is the ordinary idle-sleep case already
// handled by wakeServer()/waitForServerAwake() above, not a power outage.
export async function fetchPowerStatus() {
  try {
    const res = await fetch(`${BASE_URL}/power-status`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    return null;
  }
}

export function getIsAdmin() {
  return localStorage.getItem(IS_ADMIN_KEY) === 'true';
}

export async function request(path, options = {}) {
  const token = getSessionToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    let message;
    if (data && data.detail) {
      if (Array.isArray(data.detail)) {
        message = data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (typeof data.detail === 'string') {
        message = data.detail;
      } else {
        message = JSON.stringify(data.detail);
      }
    } else {
      message = `Request failed with status ${res.status}`;
    }
    throw new Error(message);
  }

  return data;
}

export function signup({ username, email, password, first_name, last_name, secret_question_1, secret_answer_1, secret_question_2, secret_answer_2, deviceFingerprint }) {
  return request('/auth/signup', {
    method: 'POST',
    body: JSON.stringify({
      username,
      email,
      password,
      first_name,
      last_name,
      secret_question_1,
      secret_answer_1,
      secret_question_2,
      secret_answer_2,
      device_fingerprint: deviceFingerprint,
    }),
  });
}

export function verifyEmail({ email, code }) {
  return request('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}

export function resendVerification(email) {
  return request('/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function signAgreement({ userId, documentType, signatureText }) {
  return request('/auth/sign-agreement', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, document_type: documentType, signature_text: signatureText }),
  });
}

export async function login({ username, password, deviceFingerprint }) {
  const result = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password, device_fingerprint: deviceFingerprint }),
  });
  setSessionToken(result.session_token);
  localStorage.setItem(IS_ADMIN_KEY, result.is_admin ? 'true' : 'false');
  return result;
}

// Tells go-api the session is over so it can decide (once *no* sessions remain)
// to sleep the server. Best-effort / fire-and-forget from the caller's point of
// view: local logout (clearing the token) should proceed regardless of whether
// this network call succeeds, so failures are swallowed here rather than thrown.
export async function logout() {
  try {
    await request('/auth/logout', { method: 'POST' });
  } catch (e) {
    console.warn('logout: server notify failed (logging out locally anyway)', e);
  }
}

export function getDeviceFingerprint() {
  const existing = localStorage.getItem('aetheris_device_fingerprint');
  if (existing) return existing;

  const fresh = `${navigator.userAgent}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem('aetheris_device_fingerprint', fresh);
  return fresh;
}

export function getMovies(search) {
  const query = search ? `?search=${encodeURIComponent(search)}` : '';
  return request(`/movies${query}`);
}

export function getDiscover() {
  return request('/tmdb/discover');
}

// 2026-08-16 (dedicated Anime page): /tmdb/discover-tv is a generic /discover/tv passthrough
// (go-api/handlers/tmdb.go's TMDBDiscoverTV forwards every query param as-is) -- genre 16 =
// Animation, origin_country JP is what keeps this to actual anime rather than all animated
// content worldwide. Returns TMDB's native {results: [...]} shape.
export function getAnimeDiscover() {
  return request('/tmdb/discover-tv?with_genres=16&with_origin_country=JP&sort_by=popularity.desc')
    .then(data => data.results || []);
}

export function getMovieDetails(tmdbId) {
  return request(`/tmdb/movie/${tmdbId}`);
}

export function getPersonDetails(personId) {
  return request(`/tmdb/person/${personId}`);
}

export function getStreamUrl(localMovieId) {
  const token = getSessionToken();
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : ``;
  return `/api/movies/${localMovieId}/stream${tokenParam}`;
}

export function searchMovieReleases(title, year, tmdbId) {
  const yearParam = year ? `&year=${year}` : '';
  const tmdbParam = tmdbId ? `&tmdb_id=${tmdbId}` : '';
  return request(`/search/movie?title=${encodeURIComponent(title)}${yearParam}${tmdbParam}`);
}

// One-shot "just play it" endpoint: local stream if on drive, otherwise the server
// auto-picks the best instant debrid source and background-downloads a drive copy.
export function instantPlayMovie(title, year, tmdbId, skey) {
  const yearParam = year ? `&year=${year}` : '';
  const tmdbParam = tmdbId ? `&tmdb_id=${tmdbId}` : '';
  const skeyParam = skey ? `&skey=${skey}` : '';
  return request(`/movies/instant-play?title=${encodeURIComponent(title)}${yearParam}${tmdbParam}${skeyParam}`);
}

// Search-stage polling (2026-08-22): while an instant-play call is in flight, the caller
// polls this with the same skey it passed to instantPlayMovie/instantPlayTv to get a
// cyberpunk-flavored progress phase without the server ever naming its actual sources.
export function getSearchStage(skey) {
  return request(`/search-stage/${skey}`).catch(() => ({ stage: '' }));
}

export function acquireStream({ infoHash, magnetUrl, provider, title, releaseYear, tmdbId, posterPath, backdropPath, overview, popularity, instant }) {
  return request('/stream/acquire', {
    method: 'POST',
    body: JSON.stringify({
      info_hash: infoHash,
      magnet_url: magnetUrl,
      provider,
      title,
      release_year: releaseYear,
      tmdb_id: tmdbId,
      poster_path: posterPath,
      backdrop_path: backdropPath,
      overview,
      popularity,
      instant: !!instant,
    }),
  });
}

export function getStreamProgress(infoHash) {
  return request(`/stream/progress/${infoHash}`);
}

// COMIC BOOKS page — 2026-08-26 request. Same search-and-acquire shape as ADULT below
// (Jackett primary indexer, Prowlarr fallback -- see handlers/comics.go), just downloaded
// instead of streamed since a comic is a single file (cbz/cbr/pdf), not a video.
export function searchComics(query) {
  return request(`/comics/search?query=${encodeURIComponent(query)}`);
}
export function getNewComics() {
  return request('/comics/new');
}

// ADULT page — search-and-instant-stream only, mirrors searchMovieReleases/acquireStream/
// getStreamProgress above but never touches the local library.
export function searchAdult(query) {
  return request(`/adult/search?query=${encodeURIComponent(query)}`);
}
export function getAdultNewReleases() {
  return request('/adult/new-releases');
}
export function getAdultRecommended() {
  return request('/adult/recommended');
}
// Polled by the app-wide job poller in App.jsx for any job_id a non-instant adult
// result came back with, until status is "ready" (or "error").
export function getAdultCacheStatus(jobId) {
  return request(`/adult/cache-status?job_id=${encodeURIComponent(jobId)}`);
}
// Fire-and-forget watch marker: the backend keeps the TorBox-cached copy while it's being
// watched and auto-deletes it 24h after FIRST watch (see the retention sweep in adult.go).
// Called whenever adult playback actually starts, from both play paths.
export function markAdultWatched(infoHash, title) {
  return request(`/adult/watched?info_hash=${encodeURIComponent(infoHash)}&title=${encodeURIComponent(title || '')}`, {
    method: 'POST',
  }).catch(() => {});
}
export function submitAdultVerification({ over18, birthDate }) {
  return request('/user/adult-verify', {
    method: 'POST',
    body: JSON.stringify({ over_18: over18, birth_date: birthDate || '' }),
  });
}
export function setAdultContentEnabled(enabled) {
  return request('/user/me', {
    method: 'PATCH',
    body: JSON.stringify({ adult_content_enabled: enabled }),
  });
}
export function changePassword({ currentPassword, newPassword }) {
  return request('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

// Parental controls (2026-08-09). currentPassword is omitted on first-time creation --
// the server only requires it if a hash already exists.
export function setParentalPassword({ currentPassword, newPassword }) {
  return request('/user/parental-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword || '', new_password: newPassword }),
  });
}
// Checked every time a locked section is opened -- see SideNav-gated routing in App.jsx.
export function verifyParentalPassword(password) {
  return request('/user/parental-password/verify', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}
export function setParentalLocks(sections) {
  return request('/user/parental-locks', {
    method: 'PUT',
    body: JSON.stringify({ sections }),
  });
}
export function adminResetParentalPassword(userId) {
  return request(`/admin/users/${encodeURIComponent(userId)}/parental-password/reset`, {
    method: 'POST',
  });
}
// 2026-08-29: master on/off switch for parental controls. Turning ON needs no password
// (safe to do any time); turning OFF requires the current parental password -- pass it
// only when disabling, the server ignores it when enabling.
export function setParentalEnabled(enabled, password = '') {
  return request('/user/parental-enabled', {
    method: 'POST',
    body: JSON.stringify({ enabled, password }),
  });
}

export function getActiveDownloads() {
  return request('/downloads/active');
}

export function searchPeople(query) {
  return request(`/tmdb/search/people?query=${encodeURIComponent(query)}`);
}

export function searchTmdb(title) {
  return request(`/tmdb/search?title=${encodeURIComponent(title)}`);
}

export function downloadNow({ infoHash, magnetUrl, provider, title, releaseYear, tmdbId, posterPath, backdropPath, overview }) {
  return request('/movies/download-now', {
    method: 'POST',
    body: JSON.stringify({
      info_hash: infoHash,
      magnet_url: magnetUrl,
      provider,
      title,
      release_year: releaseYear,
      tmdb_id: tmdbId,
      poster_path: posterPath,
      backdrop_path: backdropPath,
      overview,
    }),
  });
}

export function scanTvShows() {
  return request('/admin/scan-tv', { method: 'POST' });
}

export function scanGdriveMovies() {
  return request('/admin/scan-gdrive-movies', { method: 'POST' });
}

export function matchTmdb() {
  return request('/admin/match-tmdb', { method: 'POST' });
}

export function getNowPlaying() {
  return request('/tmdb/now-playing');
}

export function getCollection(collectionId) {
  return request(`/tmdb/collection/${collectionId}`);
}

export function getRecommendations(tmdbId) {
  return request(`/tmdb/movie/${tmdbId}/recommendations`);
}


export function forgotPassword(email) {
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function verifySecretAnswers({ userId, answer1, answer2 }) {
  return request('/auth/verify-secret-answers', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, secret_answer_1: answer1, secret_answer_2: answer2 }),
  });
}

export function resetPassword({ token, newPassword }) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

export function disableTotp(code) {
  return request('/auth/totp/disable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export function getTotpStatus() {
  return request('/auth/totp/status');
}

export function getDownloadStatus(tmdbId) {
  if (tmdbId === undefined || tmdbId === null || tmdbId === '') return Promise.resolve(null);
  return request(`/downloads/status/${tmdbId}`);
}

export function logMovieHistory({ tmdbId, title, posterPath }) {
  return request('/user/history/log', {
    method: 'POST',
    body: JSON.stringify({ tmdb_id: tmdbId, title, poster_path: posterPath }),
  }).catch(() => {}); // silent fail, don't interrupt streaming
}

export function getMovieHistory() {
  return request('/user/history');
}

export function addToWatchlist(item) {
  return request('/user/watchlist', {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

export function getWatchlist() {
  return request('/user/watchlist');
}

// 2026-08-17: the watchlist table/GET/POST already existed but nothing on the site ever
// called addToWatchlist (grepped every page component -- zero callers) and there was no
// remove endpoint at all. "WATCH LATER" (renamed same session from "Flagged Signals") is
// the real feature this session: a Watch Later button on MovieDetailPage/TVDetailPage, a
// remove (X) on the Dashboard's list.
export function removeFromWatchlist(tmdbId, mediaType) {
  return request(`/user/watchlist?tmdb_id=${encodeURIComponent(tmdbId)}&media_type=${encodeURIComponent(mediaType)}`, {
    method: 'DELETE',
  });
}

export function getTvLibrary() {
  return request('/tv/library');
}
export function getVaultMovies() {
  return request('/user/vault/movies');
}
export function getVaultTV() {
  return request('/user/vault/tv');
}

export function getFeaturedTrailers() {
  return request('/tmdb/featured-trailers');
}

export function getTvAiringToday() {
  return request('/tmdb/tv/airing-today');
}

// Server-side "recently streamed" (2026-08-15) -- reads user_playback_positions, so it
// reflects streams started from ANY device (Shield app included), not just this browser.
export function getTvRecentlyStreamed() {
  return request('/tv/recently-streamed');
}

// 2026-08-30 fix: AnimePage was reusing getTvRecentlyStreamed (same shared endpoint),
// which is exactly why anime and TV recently-streamed lists kept mixing -- neither list
// was ever actually anime/TV-scoped. This hits the new anime-only backend query instead
// (see AnimeRecentlyStreamed in go-api/handlers/user.go).
export function getAnimeRecentlyStreamed() {
  return request('/anime/recently-streamed');
}

// 2026-08-16 (site-wide "new message" popup) -- polled by MessagePopupNotice, mounted
// unconditionally in App.jsx so it fires no matter which page the user is on.
export function fetchNotifications() {
  return request('/user/notifications');
}
export function markNotificationRead(id) {
  return request(`/user/notifications/${id}/read`, { method: 'POST' });
}

export function searchTvShows(query) {
  return request(`/tmdb/tv/search?query=${encodeURIComponent(query)}`);
}

export function getTvDetails(tvId) {
  return request(`/tmdb/tv/${tvId}`);
}

export function getTvSeason(tvId, seasonNumber) {
  return request(`/tmdb/tv/${tvId}/season/${seasonNumber}`);
}

export function getTvRecommendations(tvId) {
  return request(`/tmdb/tv/${tvId}/recommendations`);
}

// One-shot "just play it" endpoint for TV episodes (2026-08-15), mirroring
// instantPlayMovie: local file if it's on disk, otherwise the server searches every
// indexer itself and auto-picks the fastest+best-quality instant source -- no picker
// shown, the site does the choosing. `alternatives` (unused here today, same shape the
// Android app's picker uses) carries up to 10 smaller/lower-quality fallback options in
// case a "pick a different source" UI gets added to this page later.
// 2026-08-16 (dedicated Anime page): mode='anime' hits /anime/instant-play instead of
// /tv/instant-play -- same response shape, scoped to the anime-only indexer set server-side
// (see go-api/handlers/anime.go). Lets TVDetailPage.jsx be reused as-is for the Anime page
// (same episode list/season tabs/everything) just by passing mode down to this one call.
// 2026-08-22 (per-episode quality picker): `quality` (480|720|1080|2160) makes the server
// search and play ONLY that tier — it skips the local/next-episode-cache fast paths and
// returns tier-scoped `alternatives` (used for same-tier stall retries, no 2GB ceiling).
export function instantPlayTv(title, season, episode, tmdbId, mode = 'tv', skey, quality) {
  const base = mode === 'anime' ? '/anime/instant-play' : '/tv/instant-play';
  let url = `${base}?title=${encodeURIComponent(title)}&season=${season}&episode=${episode}`;
  if (tmdbId) url += `&tmdb_id=${tmdbId}`;
  if (skey) url += `&skey=${skey}`;
  if (quality) url += `&quality=${quality}`;
  return request(url);
}

export function searchTvReleases(title, season, episode, tmdbId) {
  let url = `/search/tv?title=${encodeURIComponent(title)}`;
  if (season) url += `&season=${season}`;
  if (episode) url += `&episode=${episode}`;
  if (tmdbId) url += `&tmdb_id=${tmdbId}`;
  return request(url).then(data => {
    if (Array.isArray(data)) return data;
    return [...(data.local_results || []), ...(data.stream_results || [])];
  });
}

export function acquireTvStream({ infoHash, magnetUrl, provider, season, episode, title, tmdbId, posterPath, backdropPath, overview, instant }) {
  // (2026-08-15) Was posting to '/stream/acquire/tv', a route that was never
  // registered on the backend -- every TV instant-play attempt 404'd. The
  // real endpoint is '/stream/acquire' (same one movies use; it resolves a
  // magnet/info_hash to a stream URL via RD/TorBox and doesn't care about
  // media type). Also now forwards `instant` -- without it the backend
  // always fell through to the slow torrent-submit+poll path instead of
  // TorBox's cached fast path, even for releases already flagged instant.
  return request('/stream/acquire', {
    method: 'POST',
    body: JSON.stringify({ info_hash: infoHash, magnet_url: magnetUrl, provider, season, episode, title, tmdb_id: tmdbId, poster_path: posterPath, backdrop_path: backdropPath, overview, instant: !!instant }),
  });
}

// downloadTvNow() removed 2026-08-15: TV is stream-only by explicit user policy ("don't
// store tv shows, just stream the show directly from the link") -- no frontend caller
// left. The backend route (POST /tv/download-now, handlers.TVDownloadNow) is left in
// place rather than removed, in case anything else still calls it directly.

// ── FORUM ────────────────────────────────────────────────────────────
export function getForumMe() { return request('/forum/me'); }
export function setForumUsername(username) { return request('/forum/username', { method: 'POST', body: JSON.stringify({ username }) }); }
export function getForumCategories() { return request('/forum/categories'); }
export function getForumThreads(slug, page = 1) { return request(`/forum/categories/${slug}/threads?page=${page}`); }
export function createForumThread(slug, data) { return request(`/forum/categories/${slug}/threads`, { method: 'POST', body: JSON.stringify(data) }); }
export function getForumThread(threadId) { return request(`/forum/threads/${threadId}`); }
export function createForumReply(threadId, data) { return request(`/forum/threads/${threadId}/replies`, { method: 'POST', body: JSON.stringify(data) }); }
export function editForumThread(threadId, data) { return request(`/forum/threads/${threadId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
export function deleteForumThread(threadId) { return request(`/forum/threads/${threadId}`, { method: 'DELETE' }); }
export function editForumReply(replyId, data) { return request(`/forum/replies/${replyId}`, { method: 'PATCH', body: JSON.stringify(data) }); }
export function deleteForumReply(replyId) { return request(`/forum/replies/${replyId}`, { method: 'DELETE' }); }
export async function uploadForumImage(file) {
  const token = getSessionToken();
  const form = new FormData();
  form.append('file', file);
  const resp = await fetch(`${BASE_URL}/forum/upload-image`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: form,
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export function deleteTvEpisode({ tmdbId, season, episode }) {
  return request('/admin/tv/episode', {
    method: 'DELETE',
    body: JSON.stringify({ tmdb_id: tmdbId, season, episode }),
  });
}

export function deleteTvSeason({ tmdbId, season }) {
  return request('/admin/tv/season', {
    method: 'DELETE',
    body: JSON.stringify({ tmdb_id: tmdbId, season }),
  });
}

export function savePlaybackPosition({ tmdbId, mediaType = 'movie', season, episode, positionSeconds, durationSeconds }) {
  return request('/user/playback-position', {
    method: 'POST',
    body: JSON.stringify({
      tmdb_id: tmdbId,
      media_type: mediaType,
      season: season || null,
      episode: episode || null,
      position_seconds: positionSeconds,
      duration_seconds: durationSeconds || null,
    }),
  });
}

export function getPlaybackPosition(tmdbId, mediaType = 'movie', season, episode) {
  const params = new URLSearchParams({ media_type: mediaType });
  if (season) params.append('season', season);
  if (episode) params.append('episode', episode);
  return request(`/user/playback-position/${tmdbId}?${params}`);
}

export function getMusicCharts() { return request('/music/charts'); }
export function searchMusicLastfm(q, type = 'all') { return request(`/music/search/lastfm?q=${encodeURIComponent(q)}&type=${type}`); }
export function getMusicArtist(artistName) { return request(`/music/artist/${encodeURIComponent(artistName)}`); }
export function getMusicAlbum(artist, album) { return request(`/music/album?artist=${encodeURIComponent(artist)}&album=${encodeURIComponent(album)}`); }
export function searchMusicReleases(q) { return request(`/music/search/releases?q=${encodeURIComponent(q)}`); }
export function acquireMusic(payload) { return request('/music/acquire', { method: 'POST', body: JSON.stringify(payload) }); }
export function downloadMusic(payload) { return request('/music/download', { method: 'POST', body: JSON.stringify(payload) }); }
export function musicDownloadStatus(jobId) { return request(`/music/download/status/${jobId}`); }
export function getMusicLibrary() { return request('/music/library'); }
export function logMusicPlay(payload) { return request('/music/history/log', { method: 'POST', body: JSON.stringify(payload) }); }
export function getMusicHistory() { return request('/music/history'); }
export function deleteMusicTrack(id, dbOnly=false) { return request(`/music/track/${id}${dbOnly ? '?db_only=true' : ''}`, { method: 'DELETE' }); }
export function deleteMusicAlbum(artist, album, dbOnly=false) { return request(`/music/album?artist=${encodeURIComponent(artist)}&album=${encodeURIComponent(album)}${dbOnly ? '&db_only=true' : ''}`, { method: 'DELETE' }); }
export function deleteMusicArtist(artist, dbOnly=false) { return request(`/music/artist?artist=${encodeURIComponent(artist)}${dbOnly ? '?db_only=true' : ''}`, { method: 'DELETE' }); }

// 2026-08-29: shared resolve/on-drive helpers, pulled out of MusicPage.jsx's page-local
// copies so App.jsx's queue prefetcher (see musicPrefetch effect) can reuse the exact same
// resolve->rip->poll logic instead of re-implementing it -- keeps "is this track ready"
// answered identically everywhere it's asked.
export function getMusicOnDrive(artist) {
  return request(`/music/on-drive?artist=${encodeURIComponent(artist)}`).catch(() => []);
}

// Resolves (ripping if needed) one track and returns its on-drive id, or null if no
// source could be found anywhere. Used by PLAY ALL/SHUFFLE's queue prefetcher so a track
// is fully downloaded, cached, and scanned into the library before playback reaches it.
export async function resolveAndRipTrack(artist, album, title) {
  try {
    const onDrive = await getMusicOnDrive(artist);
    const found = onDrive.find(t => t.title.toLowerCase() === title.toLowerCase());
    if (found) return found.id;
  } catch {}
  let videoId = null;
  try {
    const d = await request(`/music/youtube?artist=${encodeURIComponent(artist)}&track=${encodeURIComponent(title)}`);
    videoId = d.video_id || null;
  } catch {}
  let jobId;
  try {
    const r2 = await request('/music/rip', { method: 'POST', body: JSON.stringify({ video_id: videoId, artist, album: album || 'Unknown Album', title }) });
    jobId = r2.job_id;
    if (!jobId) return null;
  } catch { return null; }
  for (let i = 0; i < 150; i++) {
    await new Promise(res => setTimeout(res, 2000));
    try {
      const s = await request(`/music/rip/status/${jobId}`);
      if (s.status === 'complete') {
        const fresh = await getMusicOnDrive(artist);
        const t = fresh.find(x => x.title.toLowerCase() === title.toLowerCase());
        return t ? t.id : null;
      }
      if (s.status === 'error') return null;
    } catch { return null; }
  }
  return null;
}

export function saveToLibrary(movie) {
  return request('/movies/save-to-library', {
    method: 'POST',
    body: JSON.stringify(movie),
  });
}

export function submitMagnet(payload) { return request('/user/magnet-queue', { method: 'POST', body: JSON.stringify(payload) }); }
export function getAdminMagnetQueue(status = 'pending') { return request(`/admin/magnet-queue?status=${status}`); }
export function approveMagnet(id) { return request(`/admin/magnet-queue/${id}/approve`, { method: 'POST' }); }
export function rejectMagnet(id) { return request(`/admin/magnet-queue/${id}/reject`, { method: 'POST' }); }
export function getBulletin() { return request('/bulletin'); }
export function createBulletinPost(payload) { return request('/bulletin', { method: 'POST', body: JSON.stringify(payload) }); }
export function deleteBulletinPost(id) { return request(`/bulletin/${id}`, { method: 'DELETE' }); }
export function editBulletinPost(id, payload) { return request(`/bulletin/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }); }
